import { Injectable, EnvironmentInjector, createEnvironmentInjector } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { ReportDispatcherService } from '../report-dispatcher/report-dispatcher.service';
import { REPORT_PROVIDERS } from '../report-providers';
import { IReportData } from '../../../models/reports/IReportData';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { reportItems } from '../../../models/reports/ReportNameList';
import { TranslateService } from '@ngx-translate/core';
import { reportTranslationKeys } from '../../../models/reports/ReportTranslationKeys';

@Injectable({ providedIn: 'root' })
export class PdfBufferService {

  private destroy$ = new Subject<void>();

  constructor(
    private readonly reportsCommonService: ReportsCommonService,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly translate: TranslateService
  ) {}

  /**
   * Generate PDF as byte array for a given report ID.
   * Fully decoupled — callable from any component without the report iframe.
   * Uses a lazy hidden iframe to avoid adding pdfmake to the Angular bundle.
   * Creates a scoped child injector for report services, destroyed after use.
   * @param reportId - The ReportList enum value identifying the report
   * @returns Promise<Uint8Array> - The PDF file as a byte array
   */
  async getReportPdfBytes(reportId: ReportList): Promise<Uint8Array> {
    const childInjector = createEnvironmentInjector(REPORT_PROVIDERS, this.environmentInjector);

    this.reportsCommonService.startPdfBufferGeneration();

    try {
      const reportDataPromise = firstValueFrom(
        this.reportsCommonService.pdfBufferReportData$
      );

      const reportItem = reportItems.find(item => item.id === reportId);
      const reportName = reportItem?.reportName ?? '';
      this.reportsCommonService.setSelectedReportName(reportName);

      const dispatcher = childInjector.get(ReportDispatcherService);
      dispatcher.generateReportByName(reportId);

      const reportData: IReportData = await reportDataPromise;

      return await this.buildPdfBuffer(reportData, reportName);
    } finally {
      this.reportsCommonService.endPdfBufferGeneration();
      childInjector.destroy();
    }
  }

    /**
   * Generate PDF as byte array for a given report ID.
   * Fully decoupled — callable from any component without the report iframe.
   * Uses a lazy hidden iframe to avoid adding pdfmake to the Angular bundle.
   * Creates a scoped child injector for report services, destroyed after use.
   * @param reportId - The ReportList enum value identifying the report
   * @returns Promise<Uint8Array> - The PDF file as a byte array
   */
  async getReportPdfBytesByReportName(reportName: string): Promise<Uint8Array> {
    const childInjector = createEnvironmentInjector(REPORT_PROVIDERS, this.environmentInjector);

    this.reportsCommonService.startPdfBufferGeneration();

    try {
      const reportDataPromise = firstValueFrom(
        this.reportsCommonService.pdfBufferReportData$
      );

      const reportItem = reportItems.find(item => item.reportName === reportName);
      if(!reportItem){
        console.error(`Report with name "${reportName}" not found.`);
        return new Uint8Array();
      }

      this.reportsCommonService.setSelectedReportName(reportItem.reportName ?? '');

      const dispatcher = childInjector.get(ReportDispatcherService);
      dispatcher.generateReportByName(reportItem.id);

      const reportData: IReportData = await reportDataPromise;

      return await this.buildPdfBuffer(reportData, reportItem.reportName);
    } finally {
      this.reportsCommonService.endPdfBufferGeneration();
      childInjector.destroy();
    }
  }

  /**
   * Generate a merged multi-PDF as byte array for multiple report IDs.
   * Collects IReportData for each report sequentially, then merges them in one PDF.
   * @param reportIds - Array of ReportList enum values
   * @returns Promise<Uint8Array> - The merged PDF file as a byte array
   */
  async getMultiReportPdfBytes(reportIds: ReportList[]): Promise<Uint8Array> {
    const childInjector = createEnvironmentInjector(REPORT_PROVIDERS, this.environmentInjector);
    const dispatcher = childInjector.get(ReportDispatcherService);

    this.reportsCommonService.startPdfBufferGeneration();

    try {
      const reports: { reportKey: string; reportData: IReportData }[] = [];

      for (const reportId of reportIds) {
        const reportDataPromise = firstValueFrom(
          this.reportsCommonService.pdfBufferReportData$
        );

        const reportItem = reportItems.find(item => item.id === reportId);
        const reportName = reportItem?.reportName ?? '';
        this.reportsCommonService.setSelectedReportName(reportName);

        dispatcher.generateReportByName(reportId);
        const reportData = await reportDataPromise;
        reports.push({ reportKey: reportName, reportData });
      }

      return await this.buildMultiPdfBuffer(reports);
    } finally {
      this.reportsCommonService.endPdfBufferGeneration();
      childInjector.destroy();
    }
  }

  private buildPdfBuffer(reportData: IReportData, reportName: string): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.src = '/tools/reports/pdf-worker.html';
      iframe.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute;';

      const cleanup = () => {
        globalThis.removeEventListener('message', onMessage);
        iframe.remove();
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== location.origin) return;
        if (event.data?.type === 'PDF_BUFFER_READY') {
          clearTimeout(timeout);
          cleanup();
          resolve(new Uint8Array(event.data.buffer));
        } else if (event.data?.type === 'PDF_BUFFER_ERROR') {
          clearTimeout(timeout);
          cleanup();
          reject(new Error(event.data.error));
        }
      };

      globalThis.addEventListener('message', onMessage);

      // Safety timeout
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('PDF generation timed out'));
      }, 30000);

      iframe.onload = () => {
        const checkboxes = this.reportsCommonService.getReportCheckboxesForReport(reportName);
        iframe.contentWindow!.postMessage({
          type: 'GENERATE_PDF_BUFFER',
          reportData,
          translations: this.getReportTranslations(),
          options: {
            isColorMode: checkboxes.color.checked,
            isExpanded: checkboxes.expanded.checked && !checkboxes.expanded.disabled,
            useGenericName: checkboxes.genericName.checked
          }
        }, location.origin);
      };

      iframe.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error('Failed to load PDF worker iframe'));
      };

      document.body.appendChild(iframe);
    });
  }

  private buildMultiPdfBuffer(reports: { reportKey: string; reportData: IReportData }[]): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.src = '/tools/reports/pdf-worker.html';
      iframe.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute;';

      const cleanup = () => {
        globalThis.removeEventListener('message', onMessage);
        iframe.remove();
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== location.origin) return;
        if (event.data?.type === 'PDF_BUFFER_READY') {
          clearTimeout(timeout);
          cleanup();
          resolve(new Uint8Array(event.data.buffer));
        } else if (event.data?.type === 'PDF_BUFFER_ERROR') {
          clearTimeout(timeout);
          cleanup();
          reject(new Error(event.data.error));
        }
      };

      globalThis.addEventListener('message', onMessage);

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Multi-PDF generation timed out'));
      }, 60000);

      iframe.onload = () => {
        const checkboxes = this.reportsCommonService.getReportsCheckboxes();
        iframe.contentWindow!.postMessage({
          type: 'GENERATE_MULTI_PDF_BUFFER',
          reports: reports.map(report => {
            const reportCheckboxes = this.reportsCommonService.getReportCheckboxesForReport(report.reportKey);
            return {
              ...report,
              options: { isExpanded: reportCheckboxes.expanded.checked && !reportCheckboxes.expanded.disabled }
            };
          }),
          translations: this.getReportTranslations(),
          options: {
            isColorMode: checkboxes.color.checked,
            isExpanded: checkboxes.expanded.checked,
            useGenericName: checkboxes.genericName.checked
          }
        }, location.origin);
      };

      iframe.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        reject(new Error('Failed to load PDF worker iframe'));
      };

      document.body.appendChild(iframe);
    });
  }

  private getReportTranslations(): Record<string, string> {
    return Object.fromEntries(reportTranslationKeys.map(key => [key, this.translate.instant(key)]));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Temporary helper — triggers browser download from a Uint8Array for verification */
  downloadPdfBytes(bytes: Uint8Array, filename = 'report.pdf'): void {
    const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}

